import { useEffect, useRef } from "react";

function randomBetween(min: number, max: number) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

/**
 * Studio-recorded DJ drops, generated with a neural TTS voice (see
 * scripts/generate-kali-drops.py). Served from client/public/audio/kali/.
 * The first drop is always the full intro greeting.
 */
const KALI_DROPS = [
  "/audio/kali/kali-intro.mp3",
  "/audio/kali/kali-station-id.mp3",
  "/audio/kali/kali-keep-swinging.mp3",
  "/audio/kali/kali-stay-tuned.mp3",
  "/audio/kali/kali-pickleball-vibes.mp3",
  "/audio/kali/kali-court-is-live.mp3",
  "/audio/kali/kali-turn-it-up.mp3",
  "/audio/kali/kali-dink-to-drive.mp3",
  "/audio/kali/kali-just-getting-started.mp3",
  "/audio/kali/kali-keep-the-rally.mp3",
  "/audio/kali/kali-wherever-you-play.mp3",
  "/audio/kali/kali-big-energy.mp3",
  "/audio/kali/kali-shoutout.mp3",
  "/audio/kali/kali-fresh-tracks.mp3",
  "/audio/kali/kali-no-faults.mp3",
  "/audio/kali/kali-never-stops.mp3",
  // Advertiser ad drops (Kali voice, generated from /tmp/kali-ad-drops.txt).
  // These ride the same every-3-5-songs cadence as the DJ drops.
  "/audio/kali/kali-ayce.mp3",
  "/audio/kali/kali-picklediction-deals.mp3",
  "/audio/kali/kali-picklediction-clothing.mp3",
  "/audio/kali/kali-pickleball-addiction.mp3",
  "/audio/kali/kali-hypnopickle.mp3",
  "/audio/kali/kali-hypnopickle-calm.mp3",
];

const KALI_FALLBACK_TEXT =
  "Hi! I'm kali, your DJ at KPKL Radio — sounds of the court. Thanks for tuning in. Keep playing!";

/** Cadence between pre-scheduled drops (wall-clock play time). */
const MIN_DROP_INTERVAL_MS = 12 * 60_000;
const MAX_DROP_INTERVAL_MS = 18 * 60_000;
/**
 * Per-station ad insertion (issue #51): a sponsor sweeper plays once per
 * tune-in, this many seconds after the stream starts, before Kali's drops
 * begin. Short enough to feel like a real station ID, long enough that the
 * listener hears music first.
 */
const SWEEPER_DELAY_S = 20;
/** How many drops to keep queued on the Web Audio timeline at all times. */
const SCHEDULE_AHEAD = 3;
/** Decoded-buffer cache cap (each buffer is ~1–2 MB of PCM). */
const MAX_CACHED_BUFFERS = 8;
/** Duck shape: full fade-out (music to silence under drops), with ramp times (seconds). */
const DUCK_TO_MAX = 0.0;
const DUCK_RATIO = 0.0;
const DUCK_IN_S = 0.8;
const DUCK_OUT_S = 1.2;

/** Last drop index played, so we never repeat the same sweeper back-to-back. */
let lastDropIndex = -1;

/**
 * Wind Down exclusive ad: kali's songbyuniverse + Dominick spot. Plays ONLY
 * on the Wind Down station/genre, every 3-4 songs, like a radio ad between
 * songs. All other stations keep the normal drop rotation (no Wind Down ad).
 */
const WIND_DOWN_STATION_IDS = ["wind-down", "winddown"];
const WIND_DOWN_AD_SRC = "/audio/kali/kali-winddown-songbyuniverse.mp3";
/** Song-count cadence for the Wind Down ad (every 3-4 songs). */
const WIND_DOWN_MIN_SONGS = 3;
const WIND_DOWN_MAX_SONGS = 4;

function pickDrop(isFirst: boolean): string {
  if (isFirst) return KALI_DROPS[0];
  let i = Math.floor(Math.random() * KALI_DROPS.length);
  if (i === lastDropIndex) i = (i + 1) % KALI_DROPS.length;
  lastDropIndex = i;
  return KALI_DROPS[i];
}

/** Last-resort fallback if the MP3 drops fail to load (e.g. offline). */
function speakKaliFallback() {
  if (typeof window === "undefined" || !window.speechSynthesis) return;
  window.speechSynthesis.cancel();
  const msg = new SpeechSynthesisUtterance(KALI_FALLBACK_TEXT);
  msg.rate = 1.0;
  msg.pitch = 1.1;
  msg.volume = 1.0;
  window.speechSynthesis.speak(msg);
}

/**
 * Smoothly tween an audio element's volume toward a target (unrouted
 * fallback only). Returns a cancel function.
 */
function tweenVolume(
  audio: HTMLAudioElement,
  to: number,
  ms: number
): () => void {
  const from = audio.volume;
  const steps = Math.max(4, Math.round(ms / 30));
  let step = 0;
  const timer = window.setInterval(() => {
    step += 1;
    const t = step / steps;
    audio.volume = Math.max(0, Math.min(1, from + (to - from) * t));
    if (step >= steps) window.clearInterval(timer);
  }, ms / steps);
  return () => window.clearInterval(timer);
}

/**
 * Web Audio graph the stream is routed through. Once routed, the
 * <audio> element's own output path is disconnected — user volume lives
 * on `masterGain` and stream ducking lives on `streamGain`.
 */
export interface StreamGraph {
  ctx: AudioContext;
  /** Duck automation target (stream only; Kali bypasses this). */
  streamGain: GainNode;
  /** User-facing volume once routed — write here instead of audio.volume. */
  masterGain: GainNode;
  /** Last user-selected volume (mirrors masterGain outside duck ramps). */
  userVolume: number;
}

interface Options {
  isPlaying: boolean;
  nowPlayingTitle: string | undefined;
  /** Ref to the radio stream <audio> element — routed through Web Audio. */
  streamAudioRef?: { current: HTMLAudioElement | null };
  /** Out: populated once the stream is routed — write volume to masterGain. */
  streamGraphRef?: { current: StreamGraph | null };
  /** Minimum songs between Kali intros. Default 3. */
  minSongs?: number;
  /** Maximum songs between Kali intros. Default 5. */
  maxSongs?: number;
  /**
   * Station currently tuned in (issue #51). Changing it re-arms the
   * per-station sponsor sweeper so each tune-in gets its station's ad.
   */
  stationId?: string | null;
  /**
   * Sponsor sweeper MP3 (public path) for the current station, or null when
   * the station is unsponsored. Played once per tune-in, ducked under the
   * stream like a Kali drop.
   */
  stationSweeper?: string | null;
}

interface ScheduledDrop {
  src: string;
  /** Start time on the AudioContext timeline (seconds). */
  startAtCtx: number;
  source: AudioBufferSourceNode | null;
  /** JS duck timer — only used when the stream is NOT routed through Web Audio. */
  duckTimer: number | null;
  cancelled: boolean;
  /** True for sponsor sweepers (issue #51) — one-shot, no cadence top-up. */
  isSweeper?: boolean;
  /** Decoded duration in seconds — needed to re-bake duck ramps. */
  duration: number | null;
}

/**
 * Plays a Kali voice drop every few songs while the stream is active.
 *
 * The stream element is routed through an AudioContext
 * (MediaElementSource → streamGain → masterGain → destination; drops →
 * masterGain). This is what makes background playback bulletproof on iOS:
 * the system cannot suspend the AudioContext without stopping the music
 * itself, so the graph — and everything scheduled on it — keeps running
 * with the phone locked or Safari backgrounded.
 *
 * Drops are decoded into AudioBuffers and pre-scheduled with
 * source.start(futureTime), keeping SCHEDULE_AHEAD drops queued 12–18
 * minutes apart. Duck ramps are baked into streamGain's automation
 * timeline at schedule time, so ducking works even while JS is asleep.
 *
 * Song-change metadata accelerates the cadence (drop after 3–5 songs)
 * while the page is awake. Degrades gracefully: unrouted stream (JS
 * timers + element volume), HTMLAudio drops, then Web Speech.
 */
export function useKaliBroadcast({
  isPlaying,
  nowPlayingTitle,
  streamAudioRef,
  streamGraphRef,
  minSongs = 3,
  maxSongs = 5,
  stationId = null,
  stationSweeper = null,
}: Options) {
  const prevTitleRef = useRef<string | undefined>(undefined);
  const songCountRef = useRef(0);
  const thresholdRef = useRef(randomBetween(minSongs, maxSongs));
  /** Wind Down ad cadence — own song counter so the ad lands every 3-4 songs. */
  const windDownCountRef = useRef(0);
  const windDownThresholdRef = useRef(randomBetween(WIND_DOWN_MIN_SONGS, WIND_DOWN_MAX_SONGS));
  const isFirstDropRef = useRef(true);
  /** Volume captured before a JS-driven duck (fallback paths only). */
  const preDuckVolumeRef = useRef<number | null>(null);
  const cancelDuckTweenRef = useRef<(() => void) | null>(null);
  const isPlayingRef = useRef(isPlaying);

  // ── Per-station ad insertion state (issue #51) ──
  /** Latest props, readable from timers/listeners without re-subscribing. */
  const stationIdRef = useRef<string | null>(stationId);
  const stationSweeperRef = useRef<string | null>(stationSweeper);
  /**
   * Station id whose sweeper already fired (or was skipped). Prevents a
   * replay on pause/resume; cleared implicitly by tuning to another station.
   */
  const sweeperFiredForRef = useRef<string | null>(null);
  /** HTMLAudio-fallback sweeper timer (page-awake path only). */
  const sweeperTimerRef = useRef<number | null>(null);

  // ── Web Audio state ──
  const ctxRef = useRef<AudioContext | null>(null);
  const keepAliveRef = useRef<AudioBufferSourceNode | null>(null);
  const dropGainRef = useRef<GainNode | null>(null);
  const routedRef = useRef(false);
  const buffersRef = useRef<Map<string, AudioBuffer>>(new Map());
  const pendingRef = useRef<ScheduledDrop[]>([]);
  /** Set when Web Audio init/decode fails — the HTMLAudio fallback takes over. */
  const webAudioFailedRef = useRef(false);

  // ── HTMLAudio fallback state (only used after webAudioFailedRef) ──
  const dropAudioRef = useRef<HTMLAudioElement | null>(null);
  const playMsRef = useRef(0);
  const timeThresholdRef = useRef(
    randomBetween(MIN_DROP_INTERVAL_MS, MAX_DROP_INTERVAL_MS)
  );
  const lastTickRef = useRef(0);

  const graph = () => streamGraphRef?.current ?? null;

  const resetCounters = () => {
    songCountRef.current = 0;
    thresholdRef.current = randomBetween(minSongs, maxSongs);
    playMsRef.current = 0;
    timeThresholdRef.current = randomBetween(
      MIN_DROP_INTERVAL_MS,
      MAX_DROP_INTERVAL_MS
    );
  };

  /** Reset the Wind Down ad counter (station switch / tune-in). */
  const resetWindDown = () => {
    windDownCountRef.current = 0;
    windDownThresholdRef.current = randomBetween(WIND_DOWN_MIN_SONGS, WIND_DOWN_MAX_SONGS);
  };

  const isStreamMuted = () => {
    const g = graph();
    if (g && routedRef.current) return g.userVolume <= 0;
    return (streamAudioRef?.current?.volume ?? 1) === 0;
  };

  // ── JS-driven duck/restore (fallback paths; scheduled ducks are baked) ──

  const duckStreamNow = () => {
    if (preDuckVolumeRef.current !== null) return;
    const g = graph();
    if (g && routedRef.current) {
      preDuckVolumeRef.current = g.userVolume;
      g.streamGain.gain.setTargetAtTime(
        Math.min(DUCK_TO_MAX, g.userVolume * DUCK_RATIO),
        g.ctx.currentTime,
        0.3
      );
      return;
    }
    const stream = streamAudioRef?.current;
    if (!stream) return;
    preDuckVolumeRef.current = stream.volume;
    cancelDuckTweenRef.current?.();
    cancelDuckTweenRef.current = tweenVolume(
      stream,
      Math.min(DUCK_TO_MAX, stream.volume * DUCK_RATIO),
      800
    );
  };

  const unduckStreamNow = () => {
    if (preDuckVolumeRef.current === null) return;
    const g = graph();
    if (g && routedRef.current) {
      g.streamGain.gain.setTargetAtTime(
        1,
        g.ctx.currentTime,
        0.4
      );
      preDuckVolumeRef.current = null;
      return;
    }
    const stream = streamAudioRef?.current;
    if (stream) {
      cancelDuckTweenRef.current?.();
      cancelDuckTweenRef.current = null;
      cancelDuckTweenRef.current = tweenVolume(
        stream,
        preDuckVolumeRef.current,
        1200
      );
    }
    preDuckVolumeRef.current = null;
  };

  // ── Web Audio core ──

  const getCtx = (): AudioContext | null => {
    if (webAudioFailedRef.current) return null;
    if (ctxRef.current) return ctxRef.current;
    try {
      const AC =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext?: typeof AudioContext })
          .webkitAudioContext;
      if (!AC) throw new Error("AudioContext unavailable");
      const ctx = new AC();
      ctxRef.current = ctx;
      // Keep the graph rendering even before/independently of the stream
      // route, so scheduled sources are never silently dropped.
      const silence = ctx.createBuffer(1, ctx.sampleRate, ctx.sampleRate);
      const loop = ctx.createBufferSource();
      loop.buffer = silence;
      loop.loop = true;
      const gain = ctx.createGain();
      gain.gain.value = 0;
      loop.connect(gain);
      gain.connect(ctx.destination);
      loop.start();
      keepAliveRef.current = loop;
      return ctx;
    } catch {
      webAudioFailedRef.current = true;
      return null;
    }
  };

  /**
   * Route the stream element through the AudioContext. Requires
   * crossOrigin="anonymous" on the element and CORS headers on the stream
   * (verified: all mounts send Access-Control-Allow-Origin: *). One-way
   * door — an element can only be routed once — so failures leave the
   * element untouched.
   */
  const routeStream = (ctx: AudioContext) => {
    if (routedRef.current) return;
    const audio = streamAudioRef?.current;
    if (!audio) return;
    try {
      const srcNode = ctx.createMediaElementSource(audio);
      const streamGain = ctx.createGain();
      const masterGain = ctx.createGain();
      masterGain.gain.value = audio.volume;
      const dropGain = ctx.createGain();
      dropGain.gain.value = 1.0;

      srcNode.connect(streamGain);
      streamGain.connect(masterGain);
      masterGain.connect(ctx.destination);
      dropGain.connect(masterGain); // Kali bypasses the duck, not the volume

      dropGainRef.current = dropGain;
      routedRef.current = true;
      if (streamGraphRef) {
        streamGraphRef.current = {
          ctx,
          streamGain,
          masterGain,
          userVolume: audio.volume,
        };
      }
    } catch {
      // Element already routed elsewhere or unsupported — stay unrouted;
      // drops still schedule and ducking falls back to JS timers.
    }
  };

  const getBuffer = async (
    ctx: AudioContext,
    src: string
  ): Promise<AudioBuffer | null> => {
    const cached = buffersRef.current.get(src);
    if (cached) return cached;
    try {
      const res = await fetch(src);
      const buf = await ctx.decodeAudioData(await res.arrayBuffer());
      const cache = buffersRef.current;
      if (cache.size >= MAX_CACHED_BUFFERS) {
        const oldest = cache.keys().next().value;
        if (oldest !== undefined) cache.delete(oldest);
      }
      cache.set(src, buf);
      return buf;
    } catch {
      return null;
    }
  };

  const cancelDrop = (entry: ScheduledDrop) => {
    entry.cancelled = true;
    if (entry.duckTimer !== null) window.clearTimeout(entry.duckTimer);
    if (entry.source) {
      entry.source.onended = null;
      try {
        entry.source.stop();
      } catch {
        /* already started/stopped */
      }
      try {
        entry.source.disconnect();
      } catch {
        /* noop */
      }
    }
    // A sweeper that never finished (paused mid-spot, torn down while
    // queued) gets its one-shot back for this station — the next tune-in
    // or resume re-arms it (issue #51).
    if (
      entry.isSweeper &&
      sweeperFiredForRef.current === stationIdRef.current
    ) {
      sweeperFiredForRef.current = null;
    }
    pendingRef.current = pendingRef.current.filter(p => p !== entry);
  };

  const cancelAllPending = (preserveSweepers = false) => {
    [...pendingRef.current]
      .filter(p => !(preserveSweepers && p.isSweeper))
      .forEach(cancelDrop);
    // Wipe any baked duck ramps and put the stream back at full volume.
    const g = graph();
    const ctx = ctxRef.current;
    if (g && ctx && ctx.state !== "closed") {
      g.streamGain.gain.cancelScheduledValues(0);
      g.streamGain.gain.setTargetAtTime(1, ctx.currentTime, 0.05);
      // A preserved sweeper's duck ramp was just wiped along with the rest —
      // re-bake it so the sponsor spot still ducks the stream (issue #51).
      if (preserveSweepers) {
        pendingRef.current.forEach(p => {
          if (p.isSweeper && p.duration !== null && !p.cancelled) {
            bakeDuck(ctx, g, p.startAtCtx, p.duration);
          }
        });
      }
    }
  };

  /**
   * Bake the duck ramp for one voice break into streamGain's automation
   * timeline, so ducking happens on the audio thread even while JS sleeps.
   * This gain is relative: unity restores the stream; masterGain owns volume.
   */
  function bakeDuck(
    ctx: AudioContext,
    g: StreamGraph,
    startAtCtx: number,
    duration: number
  ) {
    const gain = g.streamGain.gain;
    const duckTo = Math.min(DUCK_TO_MAX, g.userVolume * DUCK_RATIO);
    const t0 = startAtCtx;
    const t1 = startAtCtx + duration;
    gain.setValueAtTime(
      1,
      Math.max(ctx.currentTime, t0 - DUCK_IN_S)
    );
    gain.linearRampToValueAtTime(duckTo, t0);
    gain.setValueAtTime(duckTo, t1);
    gain.linearRampToValueAtTime(1, t1 + DUCK_OUT_S);
  }

  function ensureSchedule() {
    const ctx = ctxRef.current;
    if (!ctx || webAudioFailedRef.current || ctx.state !== "running") return;
    // Sweepers don't occupy cadence slots (issue #51) — the Kali drop
    // rhythm stays untouched while a sweeper rides alongside it.
    while (
      pendingRef.current.filter(p => !p.isSweeper).length < SCHEDULE_AHEAD
    ) {
      const drops = pendingRef.current.filter(p => !p.isSweeper);
      const last = drops[drops.length - 1];
      const base = last ? last.startAtCtx : ctx.currentTime;
      const gap =
        randomBetween(MIN_DROP_INTERVAL_MS, MAX_DROP_INTERVAL_MS) / 1000;
      void scheduleDropAt(base + gap);
    }
  }

  /**
   * Per-station ad insertion (issue #51): schedule the current station's
   * sponsor sweeper once, SWEEPER_DELAY_S seconds into the tune-in. Called
   * from the play lifecycle (including pause→resume, which counts as a
   * fresh listen session) and after a station switch. Pausing mid-spot
   * returns the one-shot via cancelDrop, so an interrupted ad always gets
   * another airing.
   */
  function maybeScheduleSweeper() {
    const ctx = ctxRef.current;
    if (!ctx || webAudioFailedRef.current || ctx.state !== "running") return;
    const station = stationIdRef.current;
    const src = stationSweeperRef.current;
    if (!station || !src) return; // unsponsored station
    if (sweeperFiredForRef.current === station) return; // already played
    if (pendingRef.current.some(p => p.isSweeper)) return; // one queued
    sweeperFiredForRef.current = station;
    void scheduleDropAt(ctx.currentTime + SWEEPER_DELAY_S, src);
  }

  /**
   * HTMLAudio-fallback sweeper (Web Audio unavailable): a plain JS timer
   * stands in for the AudioContext schedule — page-awake only, same
   * one-shot semantics as the Web Audio path.
   */
  const scheduleFallbackSweeperTimer = () => {
    if (sweeperTimerRef.current !== null) return;
    const station = stationIdRef.current;
    const src = stationSweeperRef.current;
    if (!station || !src) return;
    if (sweeperFiredForRef.current === station) return;
    sweeperFiredForRef.current = station;
    sweeperTimerRef.current = window.setTimeout(() => {
      sweeperTimerRef.current = null;
      if (!isPlayingRef.current) {
        // Interrupted before the spot ran — give it back.
        sweeperFiredForRef.current = null;
        return;
      }
      fallbackPlaySrc(src, true);
    }, SWEEPER_DELAY_S * 1000);
  };

  /**
   * Schedule a voice break at a future AudioContext time. By default a Kali
   * drop; pass `sweeperSrc` to insert a sponsor sweeper instead (issue #51).
   * Sweepers are one-shots: they never top up the cadence queue, and a
   * missing/undecodable sponsor file is skipped quietly instead of taking
   * the whole drop pipeline down.
   */
  async function scheduleDropAt(startAtCtx: number, sweeperSrc?: string) {
    const ctx = ctxRef.current;
    if (!ctx || webAudioFailedRef.current) return;
    const isSweeper = sweeperSrc !== undefined;
    const src = isSweeper ? sweeperSrc : pickDrop(isFirstDropRef.current);
    if (!isSweeper) isFirstDropRef.current = false;
    const entry: ScheduledDrop = {
      src,
      startAtCtx,
      source: null,
      duckTimer: null,
      cancelled: false,
      isSweeper,
      duration: null,
    };
    // Pushed synchronously so ensureSchedule sees the slot as occupied
    // while the buffer decodes.
    pendingRef.current.push(entry);

    const buf = await getBuffer(ctx, src);
    if (entry.cancelled) return;
    if (!buf) {
      cancelDrop(entry);
      if (isSweeper) return; // sponsor file missing — drops must keep working
      // Decode failed (offline / bad file) — abandon Web Audio and let the
      // HTMLAudio fallback take over from here.
      webAudioFailedRef.current = true;
      return;
    }
    // Schedule was torn down while decoding (pause/unmount).
    if (ctxRef.current !== ctx || ctx.state === "closed") return;

    const start = Math.max(startAtCtx, ctx.currentTime + 0.05);
    const g = graph();
    if (g && routedRef.current) {
      // Keep the normal bounded timeline while muted. masterGain silences
      // stream and drops together; cancelling/refilling decoded slots here
      // would spin indefinitely instead of waiting for scheduled playback.
      // Bake the duck into the audio thread: works even while JS is asleep.
      entry.duration = buf.duration;
      bakeDuck(ctx, g, start, buf.duration);
    }

    const source = ctx.createBufferSource();
    source.buffer = buf;
    source.connect(dropGainRef.current ?? ctx.destination);
    entry.source = source;
    source.onended = () => {
      pendingRef.current = pendingRef.current.filter(p => p !== entry);
      unduckStreamNow(); // no-op for baked (routed) ducks
      ensureSchedule(); // top the queue back up
    };
    // start() with a future timestamp renders on the audio thread even when
    // the page's JS is suspended — this is what makes background drops work.
    try {
      source.start(start);
    } catch {
      cancelDrop(entry);
      return;
    }

    if (!routedRef.current) {
      // Unrouted stream: duck via JS timer (page awake only).
      entry.duckTimer = window.setTimeout(
        () => {
          if (entry.cancelled) return;
          if (typeof document !== "undefined" && document.hidden) return;
          if (isStreamMuted()) {
            cancelDrop(entry);
            ensureSchedule();
            return;
          }
          duckStreamNow();
        },
        Math.max(0, (start - ctx.currentTime) * 1000 - 800)
      );
    }
  }

  /** Fire the Wind Down ad almost immediately (song-change trigger). */
  const triggerWindDownAdSoon = () => {
    const ctx = getCtx();
    if (!ctx) {
      fallbackPlaySrc(WIND_DOWN_AD_SRC);
      return;
    }
    routeStream(ctx);
    void ctx.resume().then(() => {
      if (ctx.state !== "running") {
        fallbackPlaySrc(WIND_DOWN_AD_SRC);
        return;
      }
      cancelAllPending(true);
      void scheduleDropAt(ctx.currentTime + 1.5, WIND_DOWN_AD_SRC);
      ensureSchedule();
    });
  };

  /** Fire a drop almost immediately (song-change trigger), then rebuild. */
  const triggerDropSoon = () => {
    const ctx = getCtx();
    if (!ctx) {
      fallbackPlayDrop();
      return;
    }
    routeStream(ctx);
    void ctx.resume().then(() => {
      if (ctx.state !== "running") {
        fallbackPlayDrop();
        return;
      }
      // Keep a queued sponsor sweeper (issue #51) alive across the rebuild —
      // a song change must not eat the station's ad spot.
      cancelAllPending(true);
      void scheduleDropAt(ctx.currentTime + 1.5);
      ensureSchedule();
    });
  };

  // ── HTMLAudio fallback (Web Audio unavailable or decode failed) ──

  /** Play one voice break through a plain <audio> element (fallback). */
  function fallbackPlaySrc(src: string, isSweeper = false) {
    if (typeof window === "undefined") return;
    if (isStreamMuted()) return;
    duckStreamNow();

    let drop = dropAudioRef.current;
    if (!drop) {
      drop = new Audio();
      dropAudioRef.current = drop;
    }
    drop.src = src;
    drop.volume = 1.0;

    drop.onended = () => unduckStreamNow();
    drop.onerror = () => {
      drop!.onerror = null;
      unduckStreamNow();
      // A missing sweeper is silently skipped — never speak a sponsor's copy
      // with the synthetic fallback voice.
      if (!isSweeper) speakKaliFallback();
    };
    drop.play().catch(() => unduckStreamNow());
  }

  function fallbackPlayDrop() {
    const src = pickDrop(isFirstDropRef.current);
    isFirstDropRef.current = false;
    fallbackPlaySrc(src);
  }

  const stopDrop = (restoreVolume: boolean) => {
    if (sweeperTimerRef.current !== null) {
      window.clearTimeout(sweeperTimerRef.current);
      sweeperTimerRef.current = null;
    }
    cancelAllPending();
    const drop = dropAudioRef.current;
    if (drop) {
      drop.onended = null;
      drop.onerror = null;
      drop.pause();
    }
    if (typeof window !== "undefined") window.speechSynthesis?.cancel();
    if (restoreVolume) unduckStreamNow();
  };

  // Keep live refs so timers/listeners can read the latest values
  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);

  useEffect(() => {
    stationIdRef.current = stationId;
    stationSweeperRef.current = stationSweeper;
  }, [stationId, stationSweeper]);

  // ── Song-change trigger (page awake, metadata flowing) ──
  useEffect(() => {
    if (!isPlaying || !nowPlayingTitle) return;

    // First observed title — initialize without triggering
    if (prevTitleRef.current === undefined) {
      prevTitleRef.current = nowPlayingTitle;
      return;
    }

    if (nowPlayingTitle !== prevTitleRef.current) {
      prevTitleRef.current = nowPlayingTitle;
      songCountRef.current += 1;

      // Wind Down exclusive: every 3-4 songs the kali songbyuniverse ad
      // plays INSTEAD of a normal drop (own cadence, station-gated).
      if (WIND_DOWN_STATION_IDS.includes(stationIdRef.current ?? "")) {
        windDownCountRef.current += 1;
        if (windDownCountRef.current >= windDownThresholdRef.current) {
          windDownCountRef.current = 0;
          windDownThresholdRef.current = randomBetween(WIND_DOWN_MIN_SONGS, WIND_DOWN_MAX_SONGS);
          resetCounters();
          triggerWindDownAdSoon();
          return;
        }
      }

      if (songCountRef.current >= thresholdRef.current) {
        resetCounters();
        triggerDropSoon();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying, nowPlayingTitle]);

  // ── Play/pause lifecycle: build or tear down the schedule ──
  useEffect(() => {
    if (!isPlaying) {
      // User paused / switched stations mid-drop — stop Kali, cancel every
      // queued drop, bring the music back, and free the audio session.
      stopDrop(true);
      void ctxRef.current?.suspend().catch(() => {});
      return;
    }
    const ctx = getCtx();
    if (ctx) {
      routeStream(ctx);
      void ctx
        .resume()
        .then(() => {
          ensureSchedule();
          maybeScheduleSweeper(); // per-station ad insertion (issue #51)
        })
        .catch(() => {});
    } else {
      // Web Audio unavailable — the JS-timer sweeper covers the ad spot.
      scheduleFallbackSweeperTimer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying]);

  // ── Station change: re-arm the new station's sponsor sweeper (issue #51) ──
  useEffect(() => {
    sweeperFiredForRef.current = null;
    resetWindDown();
    if (!isPlaying) return;
    const ctx = ctxRef.current;
    if (ctx && !webAudioFailedRef.current) {
      void ctx
        .resume()
        .then(maybeScheduleSweeper)
        .catch(() => {});
    } else {
      scheduleFallbackSweeperTimer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stationId]);

  // ── Wall-clock ticker — only actually fires in HTMLAudio-fallback mode ──
  useEffect(() => {
    if (!isPlaying) return;
    lastTickRef.current = Date.now();
    const timer = window.setInterval(() => {
      if (!webAudioFailedRef.current) {
        // Web Audio schedule is healthy — it owns the cadence.
        lastTickRef.current = Date.now();
        return;
      }
      // Fallback mode owns everything now — make sure the sponsor sweeper
      // still gets its slot (issue #51).
      scheduleFallbackSweeperTimer();
      const now = Date.now();
      playMsRef.current += now - lastTickRef.current;
      lastTickRef.current = now;
      if (playMsRef.current >= timeThresholdRef.current) {
        resetCounters();
        fallbackPlayDrop();
      }
    }, 15_000);
    return () => window.clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying]);

  // ── Wake-from-sleep: resume the context if iOS interrupted it ──
  useEffect(() => {
    const onVisible = () => {
      if (document.hidden || !isPlayingRef.current) return;
      const ctx = ctxRef.current;
      if (!ctx || webAudioFailedRef.current) return;
      // AudioContext.currentTime froze while suspended, so queued start
      // times self-heal — a resume is all that's needed, then top up.
      void ctx.resume().then(ensureSchedule).catch(() => {});
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Unmount cleanup
  useEffect(() => {
    return () => {
      stopDrop(true);
      try {
        keepAliveRef.current?.stop();
      } catch {
        /* noop */
      }
      void ctxRef.current?.close().catch(() => {});
      ctxRef.current = null;
      routedRef.current = false;
      if (streamGraphRef) streamGraphRef.current = null;
      dropAudioRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
